import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { LazyLoadImage, LazyLoadSource, Button, Typography } from 'ck-ui';
import styles from './DoubleHeroBanner.module.scss';
import { getSanityImg } from '../../../sanityClient';
import { getPromoName, trackPromotionClick } from '../utils';
import { usePromotionView } from '../hooks';

const DoubleHeroBanner = ({ blockDoubleHeroBanner, handle, position }) => {
  const banners = blockDoubleHeroBanner?.banners || [];
  const ref = useRef(null);

  usePromotionView(
    banners.map((item, index) => ({
      id: handle,
      name: getPromoName(
        handle,
        `${blockDoubleHeroBanner.trackingID}_${item.bannerTitle?.replace(/\s+/g, '_') || `Banner${index}`}`
      ),
      creative: getSanityImg(item.image).auto('format').url(),
      position: `${handle}-${position}-${index}`,
    })),
    ref
  );

  const renderContent = (item, index) => (
    <div className={classnames(styles.contentWrapper, { [styles.centeredContentWrapper]: blockDoubleHeroBanner.isCentered })}>
      <a
        href={item.buttonLink}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackPromotionClick({
          id: handle,
          name: getPromoName(
            handle,
            `${blockDoubleHeroBanner.trackingID}_${item.bannerTitle?.replace(/\s+/g, '_')}`
          ),
          position: `${handle}-${position}-${index}`,
        })
        }
      >
        <Typography
          renderAs="h2"
          color="white"
          className={classnames('heading3', styles.heading)}
        >
          {item.bannerTitle}
        </Typography>
      </a>

      <div>
        {item.button1 && item.buttonLink && (
          <Button
            renderAs="a"
            className={styles.first}
            href={item.buttonLink}
            secondary
            color="white"
            size="medium"
            onClick={() => trackPromotionClick({
              id: handle,
              name: getPromoName(
                handle,
                `${blockDoubleHeroBanner.trackingID}_${item.button1?.replace(/\s+/g, '_')}`
              ),
              position: `${handle}-${position}-${index}`,
            })
            }
          >
            {item.button1}
          </Button>
        )}

        {item.button2 && item.button2Link && (
          <Button
            renderAs="a"
            href={item.button2Link}
            secondary
            color="white"
            size="medium"
            onClick={() => trackPromotionClick({
              id: handle,
              name: getPromoName(
                handle,
                `${blockDoubleHeroBanner.trackingID}_${item.button2?.replace(/\s+/g, '_')}`
              ),
              position: `${handle}-${position}-${index}`,
            })
            }
          >
            {item.button2}
          </Button>
        )}
      </div>
    </div>
  );

  if (banners.length !== 2) return null;

  return (
    <section ref={ref} className={classnames('home-section', styles.root)}>
      <div
        className={classnames(
          styles.bannerWrapper,
          { [styles.centeredWrapper]: blockDoubleHeroBanner.isCentered }
        )}
      >
        {banners.map((item, index) => (
          <div className={styles.relative} key={item.title || index}>
            <a
              href={item.bannerLink}
              className={styles.image}
              onClick={() => trackPromotionClick({
                id: handle,
                name: getPromoName(
                  handle,
                  `${blockDoubleHeroBanner.trackingID}_${item.bannerTitle?.replace(/\s+/g, '_')}`
                ),
                position: `${handle}-${position}-${index}`,
              })
              }
            >
              <picture>
                <LazyLoadSource
                  srcSet={getSanityImg(item.image).auto('format').url()}
                />
                <LazyLoadImage
                  className={styles.img}
                  src={getSanityImg(item.image).auto('format').url()}
                  alt={item.bannerTitle || ''}
                />
              </picture>
            </a>

            {renderContent(item, index)}
          </div>
        ))}
      </div>
    </section>
  );
};

DoubleHeroBanner.propTypes = {
  handle: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  blockDoubleHeroBanner: PropTypes.shape({
    trackingID: PropTypes.string,
    isCentered: PropTypes.bool,
    banners: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        bannerLink: PropTypes.string,
        bannerTitle: PropTypes.string,
        image: PropTypes.object,
        button1: PropTypes.string,
        button2: PropTypes.string,
        buttonLink: PropTypes.string,
        button2Link: PropTypes.string,
      })
    ),
  }).isRequired,
};

export { DoubleHeroBanner };
